import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { usePermissions } from 'Components/Hooks/usePermissions';

export interface WithPermissionOptions<P = any> {
  module: string | ((props: P) => string);
  parent_model?: string | ((props: P) => string);
  action: string | ((props: P) => string);
  featureKey?: string | ((props: P) => string);
  tooltipId?: string | ((props: P) => string);
  skipSubscription?: boolean;
  skipPermission?: boolean;
  hideSubscription?: boolean;
  hidePermission?: boolean;
  hide?: boolean | ((props: P) => string);
}

/**
 * withPermission HOC (Adaptive with Dynamic Options):
 *
 * Wraps a component and checks its permission using options that may be provided as static
 * values or functions (to extract values from the wrapped component’s props).
 *
 * If the permission check passes then the wrapped component is rendered as usual.
 *
 * Otherwise, if the check fails and the corresponding hide flag is true,
 * the component is hidden (returns null) instead of rendered disabled with a tooltip.
 *
 * If the check fails and the hide flag is not true, then the component is rendered
 * in a disabled state with a lock icon and tooltip showing the reason.
 */
export function withPermission<P extends { disabled?: boolean; children?: React.ReactNode; className?: string }>(
  WrappedComponent: React.ComponentType<P>,
  options: WithPermissionOptions<P>
): React.FC<P> {
  return (props: P) => {
    // Resolve dynamic options.
    const moduleOption = typeof options.module === 'function' ? options.module(props) : options.module;
    const parentOption = typeof options.parent_model === 'function' ? options.parent_model(props) : options.parent_model;
    const actionOption = typeof options.action === 'function' ? options.action(props) : options.action;
    const hide = typeof options.hide === 'function' ? options.hide(props) : options.hide;
    const featureKeyOption =
      typeof options.featureKey === 'function' ? options.featureKey(props) : options.featureKey;
    const tooltipIdOption =
      typeof options.tooltipId === 'function' ? options.tooltipId(props) : options.tooltipId || 'permission-tooltip';

    // Resolve skip flags.
    const skipSubscriptionOption = options.skipSubscription ?? false;
    const skipPermissionOption = options.skipPermission ?? false;

    const { checkPermission } = usePermissions();
    // Call the permission check function.
    const permission = checkPermission(
      moduleOption,
      actionOption,
      featureKeyOption,
      skipSubscriptionOption,
      skipPermissionOption,
      parentOption
    );

    // If hide flags are set, then hide the component (return null) instead of rendering a disabled one.
    if (options.hideSubscription && permission.allowed && permission.locked) {
      return null;
    }
    if (options.hidePermission && !permission.allowed) {
      return null;
    }

    // If permission check passed (allowed and not locked) then render the wrapped component as usual.
    if (permission.allowed && !permission.locked) {
      return <WrappedComponent {...props} />;
    }

    // Otherwise, render the component as disabled with a lock icon and tooltip.
    const newChildren = (
      <div className="d-flex align-items-center w-100">
        <span className="flex-grow-1 text-truncate">{props.children}</span>
        { hide ? null : <span
          className="d-flex align-items-center justify-content-end flex-shrink-0"
          style={{ width: '2rem' }}
        >
          <i className="ri-lock-line label-icon align-middle fs-16 ms-2" />
        </span>}
      </div>
    );

    return (
      <div id={tooltipIdOption} style={{ position: 'relative', display: 'inline-block' }}>
        <WrappedComponent {...props} disabled={true}>
          {newChildren}
        </WrappedComponent>
        <UncontrolledTooltip target={tooltipIdOption} placement="top">
          {permission.reason}
        </UncontrolledTooltip>
      </div>
    );
  };
}
